import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, BreakableRenderer, BusinessComponent, BusinessComponentOptions, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { ScenarioTesterDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { CellData, CellDataOptions, ColumnConfig, ColumnConfigOptions, ColumnExtractor, ComponentData, NumberFormatter, TabData, TableData, TabUtil } from '@ddt-web/data-service';
import * as get from 'lodash/get';

import { DisclosureComponent } from '../disclaimers/disclosure-component';
import { HeadingWithDateComponent } from '../heading/heading-with-date-component';
import { ScenarioTesterColumnComposition } from '../helpers/column-compositions/scenario-tester-column-composition';
import { ScenarioHelper } from '../helpers/scenario-tester/scenario-helper';

export class MarketMovesComponent extends BusinessComponent {
	@InjectConfig(SharedConfigKey.ENABLE_SCENARIO_ADVISORY_FEE) private readonly enableScenarioAdvisoryFee: boolean = false;
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = false;
	private marketMovesTabData: TabData;
	public type: string = 'ReportComponent';

	constructor(
		public name: string,
		public dataComponentId: string,
		private defaultAumValue: number,
		private defaultScenarioKeys: string[],
		private isDollarAmountDefault: boolean = false,
		public includeYield: boolean = false,
		private advisoryFeeAmount: number = 0,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const isComponentDataSuccessful = componentData && componentData.success;
		this.marketMovesTabData = isComponentDataSuccessful && componentData.getTabById('scenarios.marketMoves');

		return TabUtil.isTabValid(this.marketMovesTabData);
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		if (this.isComponentDataVisible(componentData)) {
			const filteredTabData: TabData = ScenarioHelper.getFilteredTabData(
				componentData,
				this.defaultScenarioKeys,
				this.includeYield,
				this.enableScenarioAdvisoryFee && this.isCustomFeeEnabled,
				this.advisoryFeeAmount
			);
			const yieldData: TabData = this.getYieldData(componentData, contentSvc);
			const asOfDate: string = get(componentData, 'portfolioData[0].field.asOfDate.formattedValue', '');
			const additionalScenarioFeeText = this.advisoryFeeAmount ?
				` Scenario potential impact is net of ${this.advisoryFeeAmount}% advisory fee.` :
				` Scenario potential impact is gross of advisory fee.`;

			const marketMovesHeadlineWithYield = this.enableScenarioAdvisoryFee ? 'MARKET_MOVES_HEADLINE_WITH_YIELD_AND_SCENARIO_ADVISORY_FEE' : 'MARKET_MOVES_HEADLINE_WITH_YIELD';
			const marketMovesHeadline = this.enableScenarioAdvisoryFee ? 'MARKET_MOVES_HEADLINE_AND_SCENARIO_ADVISORY_FEE' : 'MARKET_MOVES_HEADLINE';

			const additionalDefaultText = this.enableScenarioAdvisoryFee ? additionalScenarioFeeText : '';

			return [
				new HeadingWithDateComponent().buildHeader('Market Moves Header', 'PDF_MARKET_MOVE_HEADER', 'Prepare for Market Moves', asOfDate),
				this.includeYield
					? new StaticParagraphComponent(
						marketMovesHeadlineWithYield,
						`This analysis helps you understand potential impacts to your portfolio during selected market events.
							The results of each scenario include an estimated portfolio yield, this is represented by a 12 month trailing yield.${additionalDefaultText}`,
						{},
						{ paddingBottom: 15 }
					)
					: new StaticParagraphComponent(
						marketMovesHeadline,
						`This analysis helps you understand potential impacts to your portfolio during selected market events.${additionalDefaultText}`,
						{},
						{ paddingBottom: 15 }
					),
				...this.getScenarioPanels(filteredTabData, yieldData, contentSvc),
				new DisclosureComponent(
					'SCENARIO_TESTER_DISCLAIMER_PDF',
					`<b>Past performance is not a guarantee of future results.</b>
					This analysis is strictly an illustration based on historical performance of particular securities and/or asset classes.
					The results shown are hypothetical in nature, do not reflect actual investment results and are not guarantees of future results.
					The analysis shows how a portfolio would be impacted given the application of the selected scenario.
					The hypothetical performance returns are shown for illustrative purposes only and are not intended to
					be representative of the actual performance returns of any account, portfolio or strategy.
					The hypothetical performance returns are shown gross of all fees and expenses.
					If all fees and expenses were included, the returns would be lower.
					The hypothetical performance returns do not reflect the reinvestment of all dividends,
					interest and other income. It is not likely that similar results could be achieved in the future.
					Hypothetical performance returns have inherent limitations. Unlike actual performance returns,
					they do not reflect actual trading, liquidity constraints, fees and other costs. Hypothetical
					performance returns also assume that asset allocations would not have changed over time and in
					response to market conditions, which might have occurred if an actual account had been managed
					during the time period shown. No representation is being made that any account, portfolio or
					strategy will or is likely to achieve results similar to those shown. Please see Important Information
					at the bottom of the page for more information regarding the Scenario Tester tool.
					For standardized performance of the underlying funds within the models or portfolios,
					please see the Fund Performance Table in the ‘Performance’ section. <br>
					<b>IMPORTANT: The projections or other information generated by the tool regarding
					the likelihood of various investment outcomes are hypothetical in nature,
					do not reflect actual investment results and are not guarantees of future results. Results may vary with each use and over time.</b>`,
					{},
					{ paddingBottom: 10, paddingTop: 20 }
				),
				this.includeYield
					? new DisclosureComponent(
						'SCENARIO_TESTER_DISCLAIMER_PDF_YIELD_INCLUDED',
						`12 month trailing yield for all portfolio(s) assumes that the constituents were held at
							their defined weights and does not reflect changes made in response to market conditions.
							Performance of an actual portfolio may differ significantly from the hypothetical performance
							shown for a variety of reasons, including but not limited to differences in market conditions,
							client-imposed investment restrictions, the timing of client investments and withdrawals,
							fees payable, and/or other factors. The income distributions an investor may receive in
							the future may be higher or lower than the yield shown.<br>Source: Aladdin, Morningstar`,
						{}
					)
					: new DisclosureComponent('SCENARIO_TESTER_DISCLAIMER_PDF_YIELD_NOT_INCLUDED', 'Source: Aladdin', {})
			];
		}

		return [];
	}

	private getScenarioPanels(tabData: TabData, yieldData: TabData, contentSvc: ContentService): Array<AbstractRenderer<SVGGElement>> {
		const columnConfigurations = new ScenarioTesterColumnComposition().getColumnCompositions(tabData, null, null, null, null,
			contentSvc, this.isDollarAmountDefault, this.defaultAumValue, this.includeYield, yieldData);

		if (!columnConfigurations || !columnConfigurations.headers || !columnConfigurations.body) {
			console.error(`Column configurations are not defined, table: ${this.name} will not render!`);
			return;
		}

		const border = Object.assign({}, ScenarioHelper.rowBorder);
		const bodyRows = ScenarioHelper.createScenarioBodyRows('Market Moves Body Row', columnConfigurations, border, this.includeYield);

		border.borderTopColor = ScenarioHelper.hardBorderColour;
		if (this.includeYield) {
			border.borderBottom = ScenarioHelper.thickBorder;
		}
		const headerRow: RowPanel = ScenarioHelper.createGenericTableRow('Market Moves Header Row', columnConfigurations.headers, border);

		return [new BreakableRenderer([headerRow, ...bodyRows], {}, headerRow)];
	}

	public getYieldData(componentData: ComponentData, contentSvc: ContentService): TabData {
		const yieldTabData: TabData = componentData.getTabById('scenarios.yield');
		const yieldHeadersY: CellData[] = [];
		const yieldCells: CellData[][] = [];
		let yieldTableData: TableData;

		if (this.includeYield) {
			if (yieldTabData && !yieldTabData.error) {
				yieldHeadersY.push(get(yieldTabData, 'tableData[0].headersY[0]'));
				yieldCells.push(get(yieldTabData, 'tableData[0].cells[0]', []));
			} else {
				const yieldTitle = contentSvc.get(ScenarioTesterDCR.TWELVE_MONTH_TRAILING_YIELD_KEY, ScenarioTesterDCR.TWELVE_MONTH_TRAILING_YIELD_LABEL);
				const dashValue = NumberFormatter.DASH_VALUE;

				yieldHeadersY.push(
					CellData.create({
						config: ColumnConfig.create({ label: null, infoBubble: null, extractor: null } as ColumnConfigOptions<ColumnExtractor>),
						rawValue: null,
						formattedValue: yieldTitle
					} as CellDataOptions)
				);
				yieldCells.push(
					get(this.marketMovesTabData, 'tableData[0].portfolioData', []).map(() =>
						CellData.create({
							config: ColumnConfig.create({ label: null, infoBubble: null, extractor: null } as ColumnConfigOptions<ColumnExtractor>),
							rawValue: null,
							formattedValue: dashValue
						} as CellDataOptions)
					)
				);
			}
			yieldTableData = new TableData(
				null,
				yieldHeadersY,
				yieldCells,
				get(this.marketMovesTabData, 'tableData[0].portfolioData')
			);
		}

		return new TabData(yieldTabData.config, new Array(yieldTableData), yieldTabData.displayConfig);
	}
}
